import React, { useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';

const MetricCard = ({ label, value, sub, subColor = 'text-gray-400', icon, iconBg }) => (
  <div className="stat-card rounded-2xl p-5 flex flex-col gap-2 hover:scale-[1.02] transition-transform duration-200">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center text-sm`}>
        <i className={`fas ${icon}`}></i>
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-800 tabular-nums">{value}</p>
    <p className={`text-xs font-medium ${subColor}`}>{sub}</p>
  </div>
);

const StockItem = ({ sku, onHand, min, lead, reorder, weeks }) => (
  <div className="flex items-center justify-between py-3 last:border-0" style={{ borderBottom: '1px solid var(--glass-border)' }}>
    <div>
      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{sku}</p>
      <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>On-hand {onHand} · min {min} · lead {lead}d</p>
    </div>
    <div className="text-right">
      <p className="text-lg font-bold text-blue-500 tabular-nums">{reorder} units</p>
      <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>covers {weeks}-wk demand</p>
    </div>
  </div>
);

export default function InventoryPanel() {
  const chartRef = useRef();
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();
    chartInstance.current = new Chart(chartRef.current, {
      type: 'doughnut',
      data: {
        labels: ['Critical', 'Low', 'Optimal', 'Excess'],
        datasets: [{
          data: [2, 14, 186, 31],
          backgroundColor: ['#dc2626', '#d97706', '#059669', '#3b82f6'],
          borderColor: 'transparent',
          borderWidth: 0,
          hoverOffset: 6,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, padding: 18, font: { size: 12 } }
          }
        }
      }
    });
    return () => chartInstance.current?.destroy();
  }, []);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-emerald-600 flex items-center justify-center text-white shadow-md">
          <i className="fas fa-boxes-stacked"></i>
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Inventory Intelligence</h2>
          <p className="text-xs text-gray-400">Stock risk · Reorder recommendations · EOQ analysis</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <MetricCard label="Critical items"      value="2"     sub="below min stock"   subColor="text-red-500"     icon="fa-triangle-exclamation" iconBg="bg-red-100 text-red-600" />
        <MetricCard label="Low stock"           value="14"    sub="reorder soon"      subColor="text-amber-500"   icon="fa-arrow-down"          iconBg="bg-amber-100 text-amber-600" />
        <MetricCard label="Optimal"             value="186"   sub="healthy level"     subColor="text-emerald-500" icon="fa-circle-check"        iconBg="bg-emerald-100 text-emerald-600" />
        <MetricCard label="Excess (tied capital)" value="$112k" sub="31 SKUs"        subColor="text-blue-500"    icon="fa-warehouse"           iconBg="bg-blue-100 text-blue-600" />
      </div>

      {/* Chart + Reorder list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="stat-card rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Stock Status Distribution</h3>
          <p className="text-xs text-gray-400 mb-2">233 total SKUs across all warehouses</p>
          <div className="h-56">
            <canvas ref={chartRef}></canvas>
          </div>
        </div>

        <div className="stat-card rounded-2xl p-5 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-emerald-600 flex items-center justify-center text-white text-xs">
              <i className="fas fa-truck-fast"></i>
            </div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Reorder Recommendation</h3>
            <span className="ml-auto text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full font-medium">AI Optimized</span>
          </div>
          <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
            Two items will stock out before next PO cycle. Quantities account for lead time, EOQ and 14-day demand forecast.
          </p>
          <div className="rounded-xl px-4" style={{ background: 'var(--bg-glass)' }}>
            <StockItem sku="ITM1105" onHand={12} min={50} lead={9}  reorder={500} weeks={6} />
            <StockItem sku="ITM2093" onHand={5}  min={30} lead={12} reorder={150} weeks={5} />
          </div>
          <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
            <i className="fas fa-circle-exclamation text-red-500"></i>
            Stockout avoided: <strong className="text-red-500 ml-1">$23,800</strong> in estimated lost sales
          </p>
        </div>
      </div>
    </div>
  );
}
